import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { LogIn, ShieldAlert } from "lucide-react";
import { api, configureApiAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmergentAuthBridge } from "@/auth/EmergentAuthBridge";

const AppAuthContext = createContext(null);
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || "disabled").toLowerCase();

class MemoryStore {
  constructor() {
    this.values = new Map();
  }
  get length() { return this.values.size; }
  clear() { this.values.clear(); }
  getItem(key) { return this.values.has(key) ? this.values.get(key) : null; }
  key(index) { return [...this.values.keys()][index] ?? null; }
  removeItem(key) { this.values.delete(key); }
  setItem(key, value) { this.values.set(key, value); }
}

const userStore = new WebStorageStateStore({ store: new MemoryStore() });

function SignInScreen({ error, login }) {
  return (
    <div className="min-h-screen bg-[#050B14] text-slate-100 grid place-items-center p-6">
      <div className="panel p-7 w-full max-w-md">
        <div className="font-heading text-2xl font-black tracking-tight">Portcall HQ</div>
        <p className="text-sm text-slate-400 mt-2">
          Sign in with your organization account to continue.
        </p>
        {error && (
          <div className="mt-4 sev-critical p-3 text-sm flex gap-2">
            <ShieldAlert size={17} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <Button onClick={login} className="mt-5 w-full bg-sky-600 hover:bg-sky-500 rounded-none">
          <LogIn size={15} className="mr-2" /> Sign in
        </Button>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#050B14] text-slate-400 grid place-items-center font-mono text-xs">
      Establishing secure session…
    </div>
  );
}

function OidcBridge({ children }) {
  const auth = useAuth();
  const [principal, setPrincipal] = useState(null);
  const [profileError, setProfileError] = useState("");

  const login = useCallback(() => auth.signinRedirect({
    state: { returnTo: `${window.location.pathname}${window.location.search}` },
  }), [auth]);

  const logout = useCallback(async () => {
    setPrincipal(null);
    await auth.removeUser();
    await auth.signoutRedirect();
  }, [auth]);

  useEffect(() => {
    configureApiAuth({
      getAccessToken: async () => auth.user?.access_token || null,
      onUnauthorized: () => {
        if (!auth.isLoading) login();
      },
    });
    return () => configureApiAuth();
  }, [auth.user?.access_token, auth.isLoading, login]);

  useEffect(() => {
    let cancelled = false;
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      setPrincipal(null);
      return () => { cancelled = true; };
    }
    setProfileError("");
    api.me()
      .then(value => { if (!cancelled) setPrincipal(value); })
      .catch(error => {
        if (!cancelled) {
          setProfileError(error.response?.data?.detail || "Your application role could not be verified.");
        }
      });
    return () => { cancelled = true; };
  }, [auth.isAuthenticated, auth.user?.access_token]);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(auth.isAuthenticated && principal),
    isLoading: auth.isLoading || (auth.isAuthenticated && !principal && !profileError),
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => auth.user?.access_token || null,
    login,
    logout,
  }), [auth.isAuthenticated, auth.isLoading, auth.user?.access_token, principal, profileError, login, logout]);

  if (auth.isLoading || value.isLoading) return <LoadingScreen />;
  if (auth.error || profileError) {
    return <SignInScreen error={profileError || auth.error.message} login={login} />;
  }
  if (!value.isAuthenticated) return <SignInScreen login={login} />;
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

function DevelopmentBridge({ children }) {
  const [principal, setPrincipal] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    configureApiAuth();
    let cancelled = false;
    api.me()
      .then(value => { if (!cancelled) setPrincipal(value); })
      .catch(() => { if (!cancelled) setError("The local backend is unavailable."); });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(principal),
    isLoading: !principal && !error,
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => null,
    login: async () => {},
    logout: async () => {},
  }), [principal, error]);

  if (value.isLoading) return <LoadingScreen />;
  if (error) return <SignInScreen error={error} login={() => window.location.reload()} />;
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function AppAuthProvider({ children }) {
  if (AUTH_MODE === "disabled") {
    return <DevelopmentBridge>{children}</DevelopmentBridge>;
  }

  if (AUTH_MODE === "emergent") {
    // Temporary Emergent-managed Google Auth bridge. Remove by flipping
    // REACT_APP_AUTH_MODE back to "oidc" (or "disabled") and deleting this branch.
    return <EmergentAuthBridge AuthContext={AppAuthContext}>{children}</EmergentAuthBridge>;
  }

  const authority = process.env.REACT_APP_OIDC_AUTHORITY;
  const clientId = process.env.REACT_APP_OIDC_CLIENT_ID;
  const audience = process.env.REACT_APP_OIDC_AUDIENCE;
  if (!authority || !clientId) {
    return (
      <SignInScreen
        error="OIDC frontend configuration is incomplete."
        login={() => {}}
      />
    );
  }

  const redirectUri = window.location.origin;
  const extraQueryParams = audience ? { audience } : undefined;
  return (
    <AuthProvider
      authority={authority}
      client_id={clientId}
      redirect_uri={redirectUri}
      silent_redirect_uri={redirectUri}
      post_logout_redirect_uri={redirectUri}
      response_type="code"
      scope={process.env.REACT_APP_OIDC_SCOPE || "openid profile email"}
      automaticSilentRenew
      monitorSession={false}
      loadUserInfo={false}
      userStore={userStore}
      extraQueryParams={extraQueryParams}
      onSigninCallback={user => {
        const returnTo = user?.state?.returnTo;
        window.history.replaceState({}, document.title, returnTo || "/");
      }}
    >
      <OidcBridge>{children}</OidcBridge>
    </AuthProvider>
  );
}

export function useAppAuth() {
  const value = useContext(AppAuthContext);
  if (!value) throw new Error("useAppAuth must be used inside AppAuthProvider");
  return value;
}

